import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { KadalLogo } from "@/components/KadalLogo";
import {
  Home, FolderKanban, Database, Share2,
  Settings, LogOut, Menu, X, ClipboardCheck,
  GitBranch, Plug, Activity, Bot, ChevronRight, Bell, FileText,
} from "lucide-react";
import { useState, useCallback, useRef, useEffect } from "react";
import { UserProfilePopup } from "@/components/UserProfilePopup";
import axios from "axios";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const MAIN_NAV = [
  { to: "/", icon: Home, label: "Home" },
  { to: "/projects", icon: FolderKanban, label: "Projects" },
  { to: "/templates", icon: FileText, label: "Templates" },
  { to: "/repository", icon: Database, label: "Repository" },
  { to: "/graph", icon: Share2, label: "Knowledge Graph" },
  { to: "/review", icon: ClipboardCheck, label: "Review" },
];

const ADMIN_NAV = [
  { to: "/orchestrations", icon: GitBranch, label: "Orchestrations" },
  { to: "/agents", icon: Bot, label: "Agents" },
  { to: "/integrations", icon: Plug, label: "Integrations" },
  { to: "/observability", icon: Activity, label: "Observability" },
];

const IconButton = ({ item, isActive, collapsed, onClick }) => {
  const Tag = onClick ? "button" : NavLink;
  const rowClass = `relative group flex items-center gap-3 rounded-xl ${
    collapsed ? "w-10 h-10 justify-center" : "w-full px-2 h-10"
  } ${
    isActive
      ? "bg-cobalt/[0.06]"
      : "hover:bg-foreground/[0.03]"
  }`;
  const props = onClick
    ? { onClick, className: rowClass }
    : { to: item.to, className: rowClass };

  return (
    <Tag {...props} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
        isActive
          ? "bg-cobalt text-white"
          : "border border-border/60 text-cobalt/60"
      }`}>
        <item.icon className="w-[16px] h-[16px]" strokeWidth={1.7} />
      </div>
      {!collapsed && <span className={`text-[13px] ${isActive ? "font-semibold text-foreground" : "font-medium text-foreground/60"}`}>{item.label}</span>}
      {collapsed && (
        <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-foreground text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 shadow-elevated">
          {item.label}
        </div>
      )}
    </Tag>
  );
};

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const closeProfile = useCallback(() => setShowProfile(false), []);
  const adminRef = useRef(null);
  const notifRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) {}
  }, []);

  useEffect(() => { fetchNotifications(); const iv = setInterval(fetchNotifications, 30000); return () => clearInterval(iv); }, [fetchNotifications]);


  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
      fetchNotifications();
    } catch (e) {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      fetchNotifications();
    } catch (e) {}
  };

  // Close popups on outside click
  useEffect(() => {
    const handler = (e) => {
      if (adminRef.current && !adminRef.current.contains(e.target)) setShowAdminMenu(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isAdminActive = ADMIN_NAV.some(i => location.pathname.startsWith(i.to));

  return (
    <aside
      className={`flex flex-col h-screen bg-white border-r border-border/60 shrink-0 ${expanded ? "w-52" : "w-14"}`}
      style={{ transition: "width 0.25s cubic-bezier(0.22, 1, 0.36, 1)" }}
      data-testid="sidebar"
    >
      {/* Top: Logo + Hamburger */}
      <div className={`flex items-center h-14 shrink-0 ${expanded ? "px-3 justify-between" : "justify-center"}`}>
        {expanded ? (
          <>
            <KadalLogo width={110} />
            <button onClick={() => setExpanded(false)} className="w-7 h-7 rounded-lg flex items-center justify-center text-foreground/40 hover:text-foreground hover:bg-foreground/5" data-testid="collapse-sidebar">
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button onClick={() => setExpanded(true)} className="w-10 h-10 rounded-xl flex items-center justify-center text-foreground/40 hover:text-foreground hover:bg-foreground/[0.03]" data-testid="expand-sidebar">
            <Menu className="w-[18px] h-[18px]" strokeWidth={1.7} />
          </button>
        )}
      </div>

      {/* Main nav items */}
      <nav className={`flex-1 flex flex-col gap-0.5 py-2 ${expanded ? "px-2 overflow-y-auto" : "items-center px-0 overflow-hidden"}`}>
        {MAIN_NAV?.map((item) => {
          const isActive = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
          return <IconButton key={item.to} item={item} isActive={isActive} collapsed={!expanded} />;
        })}
      </nav>

      {/* Notifications Bell */}
      <div className={`py-1 ${expanded ? "px-2" : "flex justify-center"}`} ref={notifRef}>
        <div className="relative">
          <button
            onClick={() => setShowNotifs(p => !p)}
            className={`relative group flex items-center gap-3 rounded-xl hover:bg-foreground/[0.03] ${expanded ? "w-full px-2 h-10" : "w-10 h-10 justify-center"}`}
            data-testid="notifications-bell"
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${showNotifs ? "bg-cobalt text-white" : "border border-border/60 text-cobalt/60"}`}>
              <Bell className="w-[16px] h-[16px]" strokeWidth={1.7} />
            </div>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 right-0 w-4 h-4 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center" data-testid="notif-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
            )}
            {expanded && <span className={`text-[13px] font-medium ${showNotifs ? "font-semibold text-foreground" : "text-foreground/60"}`}>Notifications</span>}
          </button>
          {showNotifs && (
            <div className="absolute left-full ml-2 bottom-0 w-80 bg-white rounded-2xl shadow-elevated border border-border/60 overflow-hidden z-50" style={{ animation: "cardFadeIn 0.15s ease-out" }} data-testid="notifications-panel">
              <div className="px-4 py-3 border-b border-border/40 flex items-center justify-between">
                <p className="text-xs font-bold text-foreground">Notifications</p>
                {unreadCount > 0 && <button onClick={markAllRead} className="text-[10px] font-semibold text-[#014d7d] hover:underline" data-testid="mark-all-read">Mark all read</button>}
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifications.length === 0 && <p className="text-xs text-foreground/45 text-center py-6">No notifications</p>}
                {notifications.slice(0, 15)?.map(n => (
                  <button key={n.id} onClick={() => markRead(n.id)}
                    className={`w-full text-left px-4 py-3 border-b border-border/20 hover:bg-foreground/[0.02] ${!n.read ? "bg-blue-50/30" : ""}`}
                    data-testid={`notif-${n.id}`}>
                    <div className="flex items-start gap-2">
                      {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-[#014d7d] mt-1.5 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className={`text-xs ${!n.read ? "font-semibold text-foreground" : "font-medium text-foreground/50"} truncate`}>{n.title}</p>
                        <p className="text-[10px] text-foreground/40 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[9px] text-foreground/50 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom */}
      <div className={`border-t border-border/40 py-2 space-y-0.5 ${expanded ? "px-2" : "flex flex-col items-center px-0"}`}>
        {/* Admin/Settings popup trigger */}
        <div className="relative" ref={adminRef}>
          <IconButton
            item={{ icon: Settings, label: "Settings" }}
            isActive={isAdminActive || location.pathname === "/settings"}
            collapsed={!expanded}
            onClick={() => setShowAdminMenu(p => !p)}
          />

          {/* Admin popup menu */}
          {showAdminMenu && (
            <div
              className="absolute bottom-full left-0 mb-1 w-56 bg-white rounded-2xl shadow-elevated border border-border/60 overflow-hidden z-50"
              style={{ animation: "cardFadeIn 0.15s ease-out" }}
              data-testid="admin-popup-menu"
            >
              <div className="px-4 py-2.5 border-b border-border/40">
                <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider">Administration</p>
              </div>
              <div className="py-1.5">
                {ADMIN_NAV?.map(item => {
                  const isActive = location.pathname.startsWith(item.to);
                  return (
                    <button
                      key={item.to}
                      onClick={() => { navigate(item.to); setShowAdminMenu(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-foreground/[0.02] ${isActive ? "text-[#014d7d] bg-[#014d7d]/5" : "text-foreground/60"}`}
                      data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
                      <span className="text-xs font-medium flex-1">{item.label}</span>
                      <ChevronRight className="w-3 h-3 opacity-25" />
                    </button>
                  );
                })}
              </div>
              <div className="border-t border-border/40 py-1.5">
                <button
                  onClick={() => { navigate("/settings"); setShowAdminMenu(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-foreground/[0.02] ${location.pathname === "/settings" ? "text-[#014d7d]" : "text-foreground/60"}`}
                  data-testid="admin-nav-general-settings"
                >
                  <Settings className="w-4 h-4 shrink-0" strokeWidth={1.7} />
                  <span className="text-xs font-medium flex-1">General Settings</span>
                  <ChevronRight className="w-3 h-3 opacity-25" />
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={logout}
          className={`relative group flex items-center gap-3 rounded-xl hover:bg-foreground/[0.03] ${expanded ? "w-full px-2 h-10" : "w-10 h-10 justify-center"}`}
          data-testid="logout-btn"
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border border-border/60 text-foreground/40">
            <LogOut className="w-[16px] h-[16px]" strokeWidth={1.7} />
          </div>
          {!expanded && (
            <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-foreground text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 shadow-elevated">Log out</div>
          )}
          {expanded && <span className="text-[13px] font-medium text-foreground/60">Log out</span>}
        </button>

        {/* User avatar */}
        <div className="relative">
          <button
            onClick={() => setShowProfile(p => !p)}
            className={`flex items-center gap-2.5 mt-1 w-full rounded-xl hover:bg-foreground/[0.03] ${expanded ? "px-3 py-2" : "justify-center py-2"}`}
            data-testid="user-avatar-btn"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6baed6] to-[#178a55] flex items-center justify-center text-white text-[11px] font-bold shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            {expanded && (
              <div className="min-w-0 text-left">
                <p className="text-xs text-foreground font-medium truncate">{user?.name}</p>
                <p className="text-[10px] text-foreground/50 truncate capitalize">{user?.role?.replace(/_/g, " ")}</p>
              </div>
            )}
          </button>
          {showProfile && <UserProfilePopup onClose={closeProfile} />}
        </div>
      </div>
    </aside>
  );
};
